import { getUiLocale, t } from './i18n';
export interface BootstrapUser {
  id: string;
  firstName: string;
  telegramFirstName: string;
  photoUrl: string | null;
  hasCustomName: boolean;
  hasCustomPhoto: boolean;
  customPhotoUpdatedAt: string | null;
  languageCode: string | null;
  preferredLocale: 'en' | 'my';
  accountStatus: 'active' | 'suspended' | 'banned';
  matchmakingEnabled: boolean;
  challengeEnabled: boolean;
  isModerator: boolean;
  isAdmin: boolean;
}

interface BootstrapResponse {
  user: BootstrapUser;
}

interface ApiErrorBody {
  error?: {
    code?: string;
    message?: string;
    requestId?: string;
  };
}

// Server messages are English; show them in the player's language. Messages that
// carry a number can't be matched word for word, so they use a fixed sentence per code.
const translateServerMessage = (message: string, code: string): string => {
  const translated = t(message);
  if (translated !== message || getUiLocale() === 'en') return translated;
  return t(({
    CARE_ACTIVE_LIMIT: 'You already have the most Care messages allowed for this person.',
    GROUP_QUORUM_NOT_READY: 'More people need to join and be ready first.',
    REPLAY_ROUNDS_INCOMPLETE: 'Every round must be revealed for a full replay.',
    INVALID_SESSION_TRANSITION: 'This game cannot do that right now.'
  } as Record<string, string>)[code] ?? message);
};

export class ApiError extends Error {
  public readonly code: string;
  public readonly requestId?: string;

  public constructor(message: string, code = 'API_ERROR', requestId?: string) {
    super(translateServerMessage(message, code));
    this.name = 'ApiError';
    this.code = code;
    this.requestId = requestId;
  }
}

export const bootstrapTelegramUser = async (initData: string): Promise<BootstrapUser> => {
  const response = await fetch('/api/v1/auth/bootstrap', {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      Authorization: `tma ${initData}`
    }
  });

  const body = (await response.json().catch(() => ({}))) as BootstrapResponse & ApiErrorBody;

  if (!response.ok) {
    throw new ApiError(
      body.error?.message ?? 'Could not start UTAGE.',
      body.error?.code ?? 'BOOTSTRAP_FAILED',
      body.error?.requestId
    );
  }

  return body.user;
};
